import React from 'react';
import PropTypes from 'prop-types';

// Material Ui
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArticleIcon from '@mui/icons-material/Article';

// Kit
import { Badge, Card, EmptyState } from '@/components/ui';

const TableStyles = {
    borderCollapse: 'separate',
    borderSpacing: 0,
    minWidth: 900,
};

const HeadCellStyle = {
    color: 'var(--muted-foreground)',
    fontWeight: 700,
    fontFamily: 'Poppins',
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backgroundColor: 'var(--secondary)',
    borderBottom: '1px solid var(--border)',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
};

const BodyCellStyle = {
    fontFamily: 'Poppins',
    fontWeight: 500,
    color: 'var(--foreground)',
    fontSize: '0.875rem',
    borderBottom: '1px solid var(--border)',
};

const AmountCellStyle = {
    ...BodyCellStyle,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
};

const formatMoney = (amount, currency) => {
    try {
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: currency || 'ARS' }).format(amount || 0);
    } catch (e) {
        return `${amount || 0} ${currency || ''}`;
    }
};

const formatDate = (value) => {
    if (!value) return '';
    // Los instantes vienen exactos de MP (debit_date) — se muestran en hora
    // argentina explícita para que el día coincida con el cobro real
    // independientemente del huso del navegador.
    return new Date(value).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
};

// Mapa de estados → texto en español + variante de Badge deck-socios.
// Verde sólo para "pagada" (éxito); ámbar pendiente; rojo fallo.
const STATUS_BADGES = {
    PENDING: { label: 'Pendiente', variant: 'warning' },
    PAID: { label: 'Pagada', variant: 'success' },
    REJECTED: { label: 'Pago fallido', variant: 'danger' },
    CANCELLED: { label: 'Cancelada', variant: 'neutral' },
    REFUNDED: { label: 'Reembolsada', variant: 'brand' },
};

const statusBadge = (status) => {
    const config = STATUS_BADGES[status];
    // Defensivo: cualquier estado desconocido cae en un badge neutro con el valor crudo.
    if (!config) {
        return <Badge variant="neutral">{status || '—'}</Badge>;
    }
    return <Badge variant={config.variant}>{config.label}</Badge>;
};

const BillingsTable = (props) => {
    const { rows, total, page, perPage, onPageChange } = props;

    if (!rows || rows.length === 0) {
        return (
            <EmptyState
                icon={ReceiptLongIcon}
                title="Todavía no tenés invoices"
                description="Cuando generemos tu próxima invoice, vas a poder descargarla desde acá — junto con el comprobante una vez que se registre el pago."
            />
        );
    }

    return (
        <Card className="overflow-hidden p-0">
          <Box sx={{ overflowX: 'auto' }} className="scroll-slim">
            <Table sx={TableStyles} aria-label="tabla de facturación">
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={HeadCellStyle}>Número</TableCell>
                        <TableCell align="left" sx={HeadCellStyle}>Fecha</TableCell>
                        <TableCell align="left" sx={HeadCellStyle}>Período</TableCell>
                        <TableCell align="right" sx={HeadCellStyle}>Monto</TableCell>
                        <TableCell align="left" sx={HeadCellStyle}>Estado</TableCell>
                        <TableCell align="right" sx={HeadCellStyle}>Descargar</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const ref = row.number || row._id;
                        // Invoice: disponible siempre que el PDF esté generado.
                        const canDownloadInvoice = row.billingStatus === 'GENERATED' && !!row.invoiceUrl;
                        // Comprobante: sólo aplica a invoices pagadas; disabled mientras se genera.
                        const isPaid = row.status === 'PAID';
                        const canDownloadReceipt = isPaid && !!row.receiptUrl;
                        // Factura fiscal (TusFacturas): tercer documento. Sólo se muestra en
                        // los estados "pagos" del ciclo fiscal; en el resto (NOT_APPLICABLE,
                        // ERROR o ausente) no aparece ni deshabilitado.
                        const fiscalStatus = row.fiscal && row.fiscal.status;
                        const canDownloadFiscal = fiscalStatus === 'EMITTED' && !!(row.fiscal && row.fiscal.fiscalPdfUrl);
                        const showFiscalButton = ['EMITTED', 'QUEUED', 'SENT', 'BLOCKED_MISSING_DATA'].includes(fiscalStatus);
                        const fiscalTooltip = fiscalStatus === 'BLOCKED_MISSING_DATA'
                            ? 'Completá tus datos fiscales'
                            : (canDownloadFiscal ? 'Descargar factura' : 'Emitiendo factura…');
                        return (
                            <TableRow key={row._id}>
                                <TableCell align="left" sx={BodyCellStyle}>{row.number || '—'}</TableCell>
                                <TableCell align="left" sx={BodyCellStyle}>{formatDate(row.from || row.createdAt)}</TableCell>
                                <TableCell align="left" sx={BodyCellStyle}>
                                    {formatDate(row.from)} - {formatDate(row.to)}
                                </TableCell>
                                <TableCell align="right" sx={AmountCellStyle}>{formatMoney(row.amount, row.currency)}</TableCell>
                                <TableCell align="left" sx={{ borderBottom: '1px solid var(--border)' }}>{statusBadge(row.status)}</TableCell>
                                <TableCell align="right" sx={{ borderBottom: '1px solid var(--border)' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                                        <Tooltip title={canDownloadInvoice ? 'Descargar invoice' : 'Generando documento…'}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Descargar invoice ${ref}`}
                                                    disabled={!canDownloadInvoice}
                                                    onClick={() => window.open(row.invoiceUrl, '_blank', 'noopener')}
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {isPaid && (
                                            <Tooltip title={canDownloadReceipt ? 'Descargar comprobante' : 'Generando comprobante…'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Descargar comprobante ${ref}`}
                                                        disabled={!canDownloadReceipt}
                                                        onClick={() => window.open(row.receiptUrl, '_blank', 'noopener')}
                                                    >
                                                        <ReceiptLongIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        {showFiscalButton && (
                                            <Tooltip title={fiscalTooltip}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Descargar factura ${ref}`}
                                                        disabled={!canDownloadFiscal}
                                                        onClick={() => window.open(row.fiscal.fiscalPdfUrl, '_blank', 'noopener')}
                                                    >
                                                        <ArticleIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[perPage]}
                            colSpan={6}
                            count={total}
                            rowsPerPage={perPage}
                            page={page}
                            onPageChange={(event, newPage) => onPageChange(newPage)}
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                            sx={{
                                '.MuiTablePagination-displayedRows': { fontFamily: 'Poppins', fontWeight: '500', color: 'var(--muted-foreground)' },
                                borderBottom: 'none',
                            }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
          </Box>
        </Card>
    );
};

BillingsTable.propTypes = {
    rows: PropTypes.array,
    total: PropTypes.number,
    page: PropTypes.number,
    perPage: PropTypes.number,
    onPageChange: PropTypes.func.isRequired,
};

BillingsTable.defaultProps = {
    rows: [],
    total: 0,
    page: 0,
    perPage: 12,
};

export default BillingsTable;
